import { useTranslation } from '@shared/core';
import type { ButtonV2Props, TextV2Props } from '@withjoy/joykit';
import { Box, ButtonV2, TextV2, Flex } from '@withjoy/joykit';
import { useMediaScreen } from '@shared/utils/media/useMediaScreens';
import type { FC } from 'react';
import React, { useCallback } from 'react';
import { styles } from './BrandCard.styles';
import { pxToRem } from '@withjoy/joykit/theme';
import type { BrandCardType } from '@apps/registry/admin/components/BrandCarousel/BrandCarousel.telemetry';
import { usePaidAdsForShopTelemetry } from '@apps/registry/admin/components/BrandCarousel/BrandCarousel.telemetry';
import { useCatalogRouting } from '@apps/registry/common/components/Catalog/useCatalogRouting';

export type BrandPromo = {
  customRedirectUrl?: string | null;
  brand: { id: string; name: string; publicUrlPath: string };
  image: { id: string; url: string };
  mobileImage?: {
    id: string;
    url: string;
  } | null;
};

type Props = {
  showShopButton?: boolean;
  showSponsoredText?: boolean;
  sponsoredTextProps?: TextV2Props;
  buttonProps?: ButtonV2Props;
  brandPromo: BrandPromo;
  brandCardType: BrandCardType;
};

const Root: FC<Props & { isMobile: boolean }> = ({
  showShopButton = true,
  buttonProps = {},
  brandPromo,
  sponsoredTextProps = {},
  showSponsoredText = true,
  brandCardType,
  isMobile,
}) => {
  const { t } = useTranslation('yourRegistry');
  const { goToBrandPage } = useCatalogRouting();
  const { shopNow, sponsored } = t('paidAds');
  const { brand, image, customRedirectUrl, mobileImage } = brandPromo;
  const { publicUrlPath, name } = brand;

  const telemetry = usePaidAdsForShopTelemetry();

  const handleCardClick = useCallback(() => {
    telemetry?.brandCardClicked({ brandName: name, cardType: brandCardType });
    if (customRedirectUrl) {
      window.open(customRedirectUrl);
    } else {
      goToBrandPage(publicUrlPath);
    }
  }, [brandCardType, name, telemetry, goToBrandPage, customRedirectUrl, publicUrlPath]);

  return (
    <Box
      width="100%"
      position="relative"
    >
      <Box
        onClick={handleCardClick}
        src={isMobile && mobileImage ? mobileImage.url : image.url}
        __css={styles.root}
        as="img"
      />
      {showShopButton && (
        <Flex
          justifyContent="center"
          alignItems="center"
          width="100%"
          position="absolute"
          bottom={6}
        >
          <ButtonV2
            as="a"
            onClick={handleCardClick}
            variant="solid"
            intent="neutral"
            typographyVariant="button2"
            marginX="auto"
            borderRadius={pxToRem(24)}
            color="mono14"
            backgroundColor="#FFFFFF"
            _active={{
              color: 'mono14',
              backgroundColor: '#FFFFFF',
            }}
            _hover={{
              color: 'mono14',
              backgroundColor: '#F0F0F0',
            }}
            {...buttonProps}
          >
            {shopNow()}
          </ButtonV2>
        </Flex>
      )}
      {showSponsoredText && (
        <TextV2
          textAlign="right"
          fontSize={pxToRem(9)}
          {...sponsoredTextProps}
        >
          {sponsored()}
        </TextV2>
      )}
    </Box>
  );
};

export const BrandCard: FC<Props> = props => {
  const isMobile = useMediaScreen({
    minScreenSize: 'mobile',
    maxScreenSize: props.brandCardType === 'productGrid' ? 'tablet' : 'mobile',
  });
  return (
    <Root
      {...props}
      isMobile={isMobile}
    />
  );
};
